import { orderBy, partition } from "lodash";
import { Metric } from "@linkfluence/models/metric";
import { QueryClient } from "@tanstack/react-query";
import { Post, TikTokPost, isTikTokPost } from "@radarly/models/posts";
import { SearchFragmentPosts, Sort } from "@radarly/models/search";

async function fetchTiktokPost(tiktokPost: TikTokPost): Promise<TikTokPost> {
  try {
    const authorId = tiktokPost.user.id;
    const videoId = tiktokPost.permalink?.split("video/")[1] || "";

    const data = await fetch(
      `https://www.tiktok.com/oembed?url=https://www.tiktok.com/@${authorId}/video/${videoId}`
    );
    const { thumbnail_url } = await data.json();
    return {
      ...tiktokPost,
      media: {
        ...tiktokPost.media,
        video: [
          {
            url: tiktokPost.media.video[0]?.url,
            thumbnail: thumbnail_url as string,
          },
        ],
      },
    };
  } catch (_) {
    return tiktokPost;
  }
}

const sortKey = {
  [Metric.Engagement]: "engagementScore",
  [Metric.Reach]: Metric.Reach,
  [Metric.Impression]: Metric.Impression,
  date: "date",
};

const getScore = (post: Post, sortBy: Sort["on"]) => post[sortKey[sortBy]] ?? 0;

export async function getEnrichedPosts(
  posts: Post[],
  fragment: SearchFragmentPosts,
  queryClient: QueryClient
): Promise<Post[]> {
  const { on: sortBy, order: sortOrder } = fragment.request.pagination.sort;
  const [tiktokPosts, classicPosts] = partition(posts, isTikTokPost);

  const cachedTiktokPosts = tiktokPosts.map((tiktokPost) =>
    queryClient.getQueryData<Post>(["tiktok-post/get", { id: tiktokPost.uid }])
  );

  const enrichedTiktokPosts = await Promise.all(
    tiktokPosts.map(async (tiktokPost, index) => {
      const cachedTiktokPost = cachedTiktokPosts[index];

      if (cachedTiktokPost) {
        return Promise.resolve(cachedTiktokPost);
      }
      const enrichedTikTokPost = await fetchTiktokPost(tiktokPost);

      queryClient.setQueryData(
        ["tiktok-post/get", { id: tiktokPost.uid }],
        enrichedTikTokPost
      );
      return Promise.resolve(enrichedTikTokPost);
    })
  );
  return orderBy(
    [...enrichedTiktokPosts, ...classicPosts],
    (post) => getScore(post, sortBy),
    sortOrder
  );
}
